import {
  SELECTED_ASKS_MAX,
  type NegotiationConcessionCategory,
  type NegotiationConcessionEntry,
  type NegotiationConcessionId,
} from "@/lib/venue-messaging/negotiation-concessions";

import type { ThreadTermId } from "./thread-state";
import type { HistoryScope } from "./types";

/** Fee names that are a resort fee under another name. */
export const RESORT_FEE_NAME_PATTERN = /resort|destination|amenity|urban|facility|property fee/i;

/**
 * Quote text that already grants a concession, so asking again would re-ask
 * for something the hotel gave. Each pattern needs a granting word next to the
 * term; a bare mention ("wifi is $15 a day") doesn't count, a negated one
 * ({@link NEGATION_PATTERN}) doesn't either, and an unmatched quote falls
 * through to the model.
 */
/**
 * What a note says when it asks for a term an org contract can rule out, so a custom ask worded
 * around the term never gets past the org's rule.
 */
export const ORG_RULED_OUT_TOPICS: Readonly<Partial<Record<NegotiationConcessionId, RegExp>>> = {
  planner_points: /\b(?:planner|loyalty|rewards?|bonus)\s+points\b/i,
};

export const GRANTED_TEXT_PATTERNS: Partial<Record<NegotiationConcessionId, RegExp>> = {
  resort_fee_waiver:
    /\b(?:resort|destination|amenity|urban|facility)\s+fees?\b[^.;\n]{0,30}\b(?:waived|included|removed|no charge)|\bno\s+(?:resort|destination|amenity|urban|facility)\s+fee/i,
  complimentary_internet:
    /\b(?:complimentary|free|included)\b[^.;\n]{0,40}\b(?:wi-?fi|internet)\b|\b(?:wi-?fi|internet)\b[^.;\n]{0,30}\b(?:complimentary|included|free|at no charge)\b/i,
  meeting_room_rental_waiver:
    /\b(?:room|meeting|space)\s+rental\b[^.;\n]{0,30}\bwaived\b|\bwaived?\b[^.;\n]{0,20}\b(?:room|meeting|space)\s+rental\b/i,
  suite_upgrades: /\bcomplimentary\b[^.;\n]{0,20}\b(?:suite\s+upgrades?|upgrades?\s+to\s+(?:a\s+)?suites?)\b/i,
  breakfast_included:
    /\bbreakfast\b[^.;\n]{0,30}\b(?:included|inclusive|complimentary)\b|\b(?:complimentary|includes|including)\b[^.;\n]{0,20}\bbreakfast\b/i,
  coffee_break_discount:
    /\bcomplimentary\b[^.;\n]{0,20}\bcoffee\s+breaks?\b|\bcoffee\s+breaks?\b[^.;\n]{0,20}\b(?:complimentary|included|at no charge)\b/i,
  welcome_drinks: /\bwelcome\s+(?:drinks?|beverages?|cocktails?|toasts?)\b/i,
  welcome_amenities: /\bwelcome\s+amenit(?:y|ies)\b/i,
  vip_amenities: /\bVIPs?\b[^.;\n]{0,20}\bamenit(?:y|ies)\b|\bamenit(?:y|ies)\b[^.;\n]{0,20}\bVIPs?\b/i,
};

/** The agency's own discount on a quote, which is not something the hotel conceded. */
export const NOWADAYS_DISCOUNT_PATTERN = /\bnowadays\b/i;

/** Discount names that grant a concession: a "F/B Discount" is the F&B discount the playbook would ask for. */
export const DISCOUNT_GRANT_PATTERNS: Partial<Record<NegotiationConcessionId, RegExp>> = {
  fb_discount: /\b(?:f\s?[/&]\s?b|food|beverage|catering|banquet|menu)\b/i,
  av_discount: /\b(?:a\s?\/?\s?v|audio|visual)\b/i,
  spa_discount: /\bspa\b/i,
  coffee_break_discount: /\bcoffee\b/i,
  reception_discount: /\breception\b/i,
};

/**
 * A negation near a grant ("Wi-Fi is not included", "rental cannot be
 * waived"), which means the quote does not grant it.
 */
export const NEGATION_PATTERN =
  /\b(?:not|no|no longer|cannot|can['’]t|won['’]t|isn['’]t|aren['’]t|excluded|excludes|unavailable)\b/i;

/** Phrases whose "no" is the grant itself ("at no charge", "no resort fee"), removed before the negation check. */
export const GRANTING_NO_PATTERN =
  /\bno\s+(?:additional\s+|extra\s+)?(?:charge|cost)\b|\bno\s+(?:resort|destination|amenity|urban|facility)\s+fees?\b/gi;

/** Characters around a grant, within its clause, that a negation may sit in. */
export const NEGATION_WINDOW_CHARS = 25;

/** "1 per 50", "1/50", "one for every 40": captures the paid-room count. Read only near {@link COMP_WORD_PATTERN}. */
export const COMP_RATIO_PATTERN =
  /\b(?:1|one)\s*(?:comp(?:limentary)?(?:\s+rooms?)?\s+)?(?:per|for every|for each|\/|:)\s*(\d{1,3})\b(?!\s*[/:]\s*\d|\s*[ap]\.?m\b)/gi;

/** The word that makes a ratio a comp ratio rather than a date or a time. */
export const COMP_WORD_PATTERN = /\bcomp(?:s|limentary)?\b/i;

/** Characters around a ratio, within its clause, where the word comp must appear. */
export const COMP_WORD_WINDOW_CHARS = 30;

/** Smallest paid-room count a comp ratio can have; "1/5" or "1:00" is something else. */
export const COMP_RATIO_MIN = 10;

/** A deposit clause in the quote's free text. */
export const DEPOSIT_PATTERN = /\bdeposit\b/i;

/** An amenities list that names a spa. */
export const SPA_PATTERN = /\bspa\b/i;

/** A hotel's amenities listing a gym ("Fitness center", "Health club"). */
export const GYM_PATTERN = /\b(?:gym|fitness|health club|exercise)/i;

/** A hotel's amenities listing any parking. */
export const PARKING_PATTERN = /\bparking\b/i;

/** A hotel's amenities listing parking on site at no charge ("Free Onsite Parking", "Free parking on premises"). */
export const FREE_PARKING_PATTERN = /\bfree (?:onsite parking|parking on premises)\b/i;

/** A hotel's amenities listing valet ("Valet Parking Service"). */
export const VALET_PATTERN = /\bvalet\b/i;

/** Longest free-text value an ask's current or target term carries. */
export const TERM_VALUE_MAX_CHARS = 80;

/** Most findings shown before the plan; the rest are left out, least relevant first. */
export const NEGOTIATION_FINDINGS_MAX = 6;

/**
 * A clause that leads with a percentage ("85% nightly", "100% due at signing"):
 * the percent, then whatever follows it up to the first clause break.
 */
export const LEADING_PERCENT_PATTERN = /^(\d{1,3}(?:\.\d+)?)\s?%\s*([^,;.\n]*)/;

/** An attrition term short enough to read as one phrase: "85% nightly", "80% cumulative", "80%". */
export const ATTRITION_SHORT_PATTERN = /^\d{1,3}(?:\.\d+)?\s?%(?:\s+(?:nightly|cumulative|daily|per night))?$/i;

/** The first percentage in an attrition clause. */
export const ATTRITION_PERCENT_PATTERN = /(\d{1,3}(?:\.\d+)?)\s?%/;

/** An attrition percentage below this is the slippage allowed, not the pickup owed. */
export const ATTRITION_SLIPPAGE_MAX_PERCENT = 50;

/** Longest hotel clause a finding repeats word for word; a longer one is left to the plan. */
export const FINDING_CLAUSE_MAX_CHARS = 40;

/** How a finding names a concession other quotes include, where the term label reads as the charge itself. */
export const FINDING_ELSEWHERE_LABELS: Partial<Record<NegotiationConcessionId, string>> = {
  complimentary_internet: "wifi",
  resort_fee_waiver: "no resort fee",
  meeting_room_rental_waiver: "waived meeting room rental",
};

/** A waived-rental note that ties the waiver to food and beverage spend. */
export const RENTAL_TIED_TO_FB_PATTERN = /\b(?:f\s?&\s?b|food|beverage|catering|banquet|minimum|spend)\b/i;

/** A text target that drops the whole charge ("Waived", "Waived against the F&B minimum"), not part of it. */
export const WAIVED_TARGET_PATTERN = /^(?:fully\s+)?(?:waived?|removed|eliminated|complimentary|free|no charge)\b/i;

/** Milliseconds in a day, for lead-time math. */
export const MS_PER_DAY = 86_400_000;

/** Past negotiations per scope that feed one plan: this hotel's weigh most, so it keeps the most. */
export const HISTORY_MAX_EVENTS_PER_SCOPE: Readonly<Record<HistoryScope, number>> = {
  this_hotel: 3,
  same_brand: 2,
  same_chain: 2,
  same_city: 2,
};

/** Newest sent negotiation rows that learned readiness counts. */
export const CONCESSION_STATS_REQUEST_SCAN_LIMIT = 2000;

/** How long one server process reuses the learned-readiness record before reading it again. */
export const CONCESSION_STATS_CACHE_TTL_MS = 5 * 60_000;

/** Quote versions read for those negotiations' before-and-after diff, per batch of pairs. */
export const CONCESSION_STATS_QUOTE_SCAN_LIMIT = 5000;

/** (event, hotel) pairs filtered in one quote-version read, so the filter stays a short URL. */
export const CONCESSION_STATS_PAIR_BATCH = 50;

/** Newest sent negotiation rows read per scan (this hotel, its brand or chain, and every hotel) to find those events. */
export const HISTORY_REQUEST_SCAN_LIMIT = 500;

/** Longest hotel excerpt kept per message. */
export const HISTORY_EXCERPT_MAX_CHARS = 1200;

/** Hotel messages kept per past event. */
export const HISTORY_EXCERPTS_PER_EVENT = 3;

/** Longest changed-text note in a quote delta. */
export const HISTORY_DELTA_TEXT_MAX_CHARS = 160;

/** Quotes read per past candidate to diff first against latest. */
export const HISTORY_QUOTES_PER_CANDIDATE = 20;

/** Words in a hotel's name that name no hotel on their own, so they are never redacted alone. */
export const VENUE_NAME_STOPWORDS: ReadonlySet<string> = new Set([
  "and",
  "the",
  "hotel",
  "hotels",
  "resort",
  "resorts",
  "inn",
  "suites",
  "spa",
  "lodge",
  "club",
  "house",
  "center",
  "centre",
  "conference",
  "convention",
  "downtown",
  "airport",
  "beach",
  "golf",
]);

/**
 * Words of another hotel's name that also read as everyday words or places in an email ("the Grand
 * Ballroom", "Even so", "Times Square"), so on their own they name nobody.
 */
export const EVERYDAY_HOTEL_NAME_WORDS: ReadonlySet<string> = new Set([
  "grand",
  "royal",
  "collection",
  "even",
  "times",
  "square",
  "place",
  "points",
  "park",
  "plaza",
  "city",
  "new",
  "north",
  "south",
  "east",
  "west",
  "central",
  "tower",
  "towers",
  "waterfront",
  "view",
  "garden",
  "gardens",
  "for",
  "with",
  "from",
  "near",
  "over",
  "under",
  "our",
  "your",
  "san",
  "santa",
  "saint",
  "los",
  "las",
  "fort",
  "port",
  "mount",
  "old",
  "town",
  "valley",
  "mountain",
  "mountains",
  "desert",
  "great",
  "lake",
  "lakes",
  "bay",
  "river",
  "harbor",
  "harbour",
  "island",
  "canyon",
  "creek",
  "ranch",
  "springs",
  "hill",
  "hills",
  "point",
  "village",
  "avenue",
  "street",
  "quarter",
  "district",
  "market",
  "arts",
  "historic",
  "country",
  "landing",
  "marina",
  "shores",
  "sands",
  "cove",
  "ridge",
  "pines",
  "palms",
  "sea",
  "ocean",
  "coast",
  "bridge",
  "station",
  "terrace",
  "court",
  "hall",
  "manor",
  "villa",
  "villas",
  "retreat",
  "forest",
  "commons",
  "state",
  "national",
  "union",
  "capitol",
  "main",
]);

/**
 * How long a tab keeps an offer's plan for reopening: long enough to cover a close and reopen, short
 * enough that a hotel reply or a quote update rarely lands in between.
 */
export const PLAN_CACHE_TTL_MS = 2 * 60_000;

/** Shortest person-name token redacted on its own; shorter tokens collide with ordinary words. */
export const REDACT_MIN_TOKEN_CHARS = 3;

/**
 * Words of a client org's or event's name that name nobody on their own ("Acme Partners", "Annual
 * Sales Kickoff"), so a hotel email's own "Sales Team" or "the event" never reads as the client.
 */
export const CLIENT_NAME_STOPWORDS: ReadonlySet<string> = new Set([
  "and",
  "the",
  "for",
  "inc",
  "llc",
  "ltd",
  "plc",
  "corp",
  "corporation",
  "company",
  "companies",
  "group",
  "partners",
  "partnership",
  "holdings",
  "international",
  "global",
  "worldwide",
  "services",
  "solutions",
  "systems",
  "technologies",
  "technology",
  "tech",
  "software",
  "labs",
  "capital",
  "ventures",
  "enterprise",
  "enterprises",
  "industries",
  "associates",
  "foundation",
  "institute",
  "agency",
  "consulting",
  "network",
  "media",
  "health",
  "care",
  "financial",
  "bank",
  "team",
  "staff",
  "client",
  "customer",
  "event",
  "events",
  "meeting",
  "meetings",
  "offsite",
  "onsite",
  "retreat",
  "summit",
  "conference",
  "convention",
  "kickoff",
  "kick",
  "off",
  "annual",
  "quarterly",
  "monthly",
  "weekly",
  "sales",
  "leadership",
  "executive",
  "board",
  "strategy",
  "training",
  "workshop",
  "planning",
  "session",
  "sessions",
  "seminar",
  "forum",
  "symposium",
  "expo",
  "incentive",
  "outing",
  "getaway",
  "trip",
  "program",
  "dinner",
  "lunch",
  "party",
  "celebration",
  "gala",
  "awards",
  "reunion",
  "holiday",
  "launch",
  "week",
  "day",
  "days",
  "year",
  "spring",
  "summer",
  "fall",
  "winter",
  "all",
  "hands",
  "product",
  "engineering",
  "marketing",
  "finance",
  "operations",
  "design",
  "research",
  "development",
]);

/**
 * Names that are also everyday words. A Nowadays staff name made of one is
 * not redacted on its own, so "Sun" from a staff signature never masks "Sun
 * deck" in a hotel's reply.
 */
export const COMMON_WORD_NAMES: ReadonlySet<string> = new Set([
  "april",
  "art",
  "august",
  "bell",
  "bill",
  "black",
  "brown",
  "case",
  "cash",
  "chase",
  "cook",
  "dale",
  "dawn",
  "drew",
  "faith",
  "ford",
  "fox",
  "frank",
  "glen",
  "grace",
  "grant",
  "green",
  "hall",
  "hill",
  "holly",
  "hope",
  "hunter",
  "ivy",
  "joy",
  "june",
  "king",
  "lake",
  "lane",
  "long",
  "love",
  "mark",
  "may",
  "miles",
  "page",
  "park",
  "pat",
  "pearl",
  "penny",
  "price",
  "ray",
  "reed",
  "rich",
  "rose",
  "ruby",
  "sage",
  "sky",
  "stone",
  "summer",
  "sun",
  "wade",
  "west",
  "white",
  "will",
  "winter",
  "wood",
  "young",
]);

/** Money in free text: symbols, codes, cents, thousands, and numbers priced per night or per person. */
export const MONEY_PATTERNS: readonly RegExp[] = [
  /[$€£¥]\s?\d[\d,]*(?:\.\d+)?(?:\s?[kKmM]\b)?/g,
  /\b(?:USD|EUR|GBP|CAD|AUD|CHF|MXN)\s?\d[\d,]*(?:\.\d+)?(?:\s?[kKmM]\b)?/gi,
  /\b\d[\d,]*(?:\.\d+)?\s?[kKmM]?\s?(?:USD|EUR|GBP|CAD|AUD|CHF|MXN|dollars?|euros?|pounds?)\b/gi,
  /\b\d[\d,]*\.\d{2}\b/g,
  /\b\d{1,3}(?:,\d{3})+\b/g,
  /\b\d+(?:\.\d+)?(?=\s*(?:\+\+\s*)?(?:per|\/|a)\s*(?:night|room|person|guest|day|head)\b)/gi,
];

/**
 * Any other number of two or more digits in another event's text ("we can do
 * 179", "189++", "45000"). Leaves percentages, decimals before a percent sign,
 * times, ratios ("1/50", "1:40") and ratio denominators ("1 per 40", "one
 * for every 40"), since history may reach the client as relative numbers.
 */
export const BARE_NUMBER_PATTERN =
  /(?<![\d.,/:]|\b(?:per|every|each)\s{1,3})\d{2,}(?:[.,]\d+)*(?!\s*(?:%|percent\b)|\s*[/:]\s*\d|[\d.,]*\d)/gi;

/** Email addresses, links and phone numbers in free text. */
export const CONTACT_PATTERNS: readonly RegExp[] = [
  /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g,
  /\bhttps?:\/\/\S+/gi,
  /\bwww\.\S+/gi,
  /\+?\d[\d\s().-]{8,}\d/g,
];

/** The model that ranks a negotiation plan. */
export const NEGOTIATION_PLAN_MODEL = "claude-sonnet-5-5";

/** Reasoning effort for the plan call. */
export const NEGOTIATION_PLAN_EFFORT = "medium";

/** Output cap for the plan call: always-on thinking plus one JSON plan ranked from a bank of about 60. */
export const NEGOTIATION_PLAN_MAX_TOKENS = 16_000;

/**
 * The plan call's timeout. One try of up to 260 seconds ends before the
 * route's 300, with time left for the loads before it.
 */
export const NEGOTIATION_PLAN_TIMEOUT_MS = 260_000;

/** No retry: a timed-out retry would outlast the route, and a failed plan offers "Try again" in the modal. */
export const NEGOTIATION_PLAN_MAX_RETRIES = 0;

/** Longest open-items text from the quote placed in the prompt. */
export const NEGOTIATION_OPEN_ITEMS_MAX_CHARS = 800;

/** Longest offer terms block (the quote's concessions, notes, attrition, cancellation and deposit wording) in the prompt. */
export const OFFER_TERMS_TEXT_MAX_CHARS = 4000;

/** Longest past negotiation excerpts block in the prompt. */
export const HISTORY_EXCERPTS_TEXT_MAX_CHARS = 8000;

/** Longest client event-form notes (other factor, flexibility, F&B needs) placed in the prompt. */
export const NEGOTIATION_CLIENT_NOTES_MAX_CHARS = 600;

/** Org contract clauses read for one plan. */
export const ORG_TERMS_MAX = 40;

/** Longest single org clause placed in the prompt: its type, title and description together. */
export const ORG_TERM_MAX_CHARS = 300;

/** Longest org contract terms block placed in the prompt. */
export const ORG_TERMS_TEXT_MAX_CHARS = 4000;

/** Messages of this candidate's own thread the plan reads, newest last. */
export const HOTEL_THREAD_MAX_MESSAGES = 8;

/** Longest single thread message placed in the prompt. */
export const HOTEL_THREAD_MESSAGE_MAX_CHARS = 1200;

/** Longest thread block placed in the prompt. */
export const HOTEL_THREAD_TEXT_MAX_CHARS = 8000;

/** The line an email's sign-off starts on; the signature below it carries no terms. */
export const SIGN_OFF_PATTERN =
  /^[ \t]*(?:--[ \t]*$|(?:best|kind|warm|warmest|many)?[ \t]*regards\b|best(?: wishes)?,?[ \t]*$|thanks?(?: you)?(?: so much)?[,!.]?[ \t]*$|cheers,?[ \t]*$|sincerely\b|sent from my\b)/im;

/** Most asks one plan carries, musts and nice-to-haves together; the validator drops the rest from the end. */
export const NEGOTIATION_PLAN_MAX_ASKS = SELECTED_ASKS_MAX;

/** Longest `why` or `fallback` kept from the model. */
export const NEGOTIATION_WHY_MAX_CHARS = 400;

/** Longest summary kept from the model. */
export const NEGOTIATION_SUMMARY_MAX_CHARS = 400;

/** A capitalized first name, as a hotel signs an email. */
export const FIRST_NAME_PATTERN = /^\p{Lu}[\p{L}'-]{0,29}$/u;

/** A Nowadays staff address; staff names and company are not client data, so history never redacts them. */
export const NOWADAYS_STAFF_EMAIL_PATTERN = /@nowadays\.ai\s*$/i;

/** Nowadays' own name ("Nowadays", "Nowadays Inc"), which the plan names in "past Nowadays negotiations". */
export const NOWADAYS_NAME_PATTERN = /^\s*nowadays\b/i;

/** A team mailbox rather than a person ("Grand Hyatt Sales", "Group Reservations"). */
export const GENERIC_SENDER_PATTERN =
  /\b(?:hotel|resort|sales|events?|groups?|reservations?|team|office|info|catering|marketing|no-?reply|concierge|rfp|meetings?)\b/i;

/** Status line once the model is choosing asks, before its first one arrives. */
export const RANKING_STEP_FINDING = "Picking the strongest asks";

/** A custom request's `why` when the model's would cite an invented history amount; it has no playbook landing. */
export const CUSTOM_REQUEST_FALLBACK_WHY = "Your org's terms or the hotel's promotion call for this.";

/** A signed figure further than this from the first quote, in percent, compares different things and is left out. */
export const SIGNED_MOVE_MAX_PERCENT = 60;

/** Newest signed bookings, and newest answered contract asks, read for one hotel's contract history. */
export const CONTRACT_HISTORY_SCAN_LIMIT = 500;

/** Quote versions read per batch of signed (event, hotel) pairs to find each pair's first quote. */
export const CONTRACT_HISTORY_QUOTE_SCAN_LIMIT = 2000;

/** Signed (event, hotel) pairs filtered in one first-quote read, so the filter stays a short URL. */
export const CONTRACT_HISTORY_PAIR_BATCH = 50;

/** A hotel reason's own "Still open from our last round:" lead; the email adds that mark to a re-ask itself. */
export const REASK_LEAD_PATTERN = /^still open\b[^:.]{0,40}[:.]\s*/i;

/** What the hotel reads on an ask whose model reason would leak, when no code reason fits the concession. */
export const GENERIC_HOTEL_REASON = "This would help the group move forward with the booking.";

/** The `why` of an org-required ask the model left out and code added back. */
export const ORG_TERM_WHY = "Your company's contract terms require this.";

/** What the client and the hotel read on a resort fee waiver the model left out and code added back. */
export const RESORT_FEE_ASK_REASONS = {
  why: "The quote adds a resort fee to every room night, and hotels often cut or waive it for a group.",
  hotelReason: "The group will spend its days in our program and make little use of what the fee covers.",
} as const;

/** A plan with fewer asks than this gets perk candidates appended as nice-to-haves until it has this many. */
export const NEGOTIATION_PLAN_FILL_TARGET = 6;

/**
 * Most asks an opening plan starts with, the org's and the client's own left uncounted, so the email
 * stays short; pruning drops the least important freebies past it.
 */
export const NEGOTIATION_PLAN_DEFAULT_ASKS = 8;

/** Most perk freebies an opening plan starts with, the client's own picks left uncounted. */
export const NEGOTIATION_PLAN_PERKS_MAX = 3;

/** Most perks hotels have granted when asked that code adds to a plan the model left them out of. */
export const GRANTED_PERKS_ADDED_MAX = 2;

/** The catalog categories whose candidates can fill a short plan. */
export const PERK_FILL_CATEGORIES = ["rooms", "perks"] as const satisfies readonly NegotiationConcessionCategory[];

/** A catalog id in one of the {@link PERK_FILL_CATEGORIES}. */
export type PerkConcessionId = Extract<
  NegotiationConcessionEntry,
  { category: (typeof PERK_FILL_CATEGORIES)[number] }
>["id"];

/** The `why` of a perk code appended to fill a short plan. */
export const PERK_FILL_WHY = "Hotels often grant this at this stage.";

/** What the hotel reads on a perk code appended to fill a short plan: no client name, no figure. */
export const PERK_FILL_HOTEL_REASONS: Readonly<Record<PerkConcessionId, string>> = {
  comp_rooms: "A comp room ratio is standard for a group block of this size.",
  suite_upgrades: "A few suites at the group rate would house the group's leaders and speakers.",
  preplanning_room_nights: "Comp nights for a planning visit help us finalize the program on site.",
  staff_rooms: "Discounted staff rooms keep the event team on property.",
  hospitality_suite: "A hospitality suite gives the team a workspace during the program.",
  breakfast_included: "Breakfast in the rate keeps attendees on property each morning.",
  complimentary_internet: "Attendees need reliable wifi to work during the program.",
  spa_discount: "A spa discount is a welcome extra for attendees.",
  planner_points: "Planner points are customary on a group booking.",
  self_parking: "Several attendees may drive in.",
  valet: "Several attendees may drive in.",
  welcome_amenities: "A welcome amenity sets the tone for the group's arrival.",
  vip_amenities: "A few VIPs will join the group.",
  early_late_checkout: "Some VIPs will arrive early or leave late around the program.",
  gym_access: "Attendees will want the fitness center during their stay.",
  package_handling: "The team will ship event materials ahead of arrival.",
  signage: "The program needs signage to direct attendees.",
  airport_shuttle: "Most attendees will fly in.",
  room_drop_fees: "The group may deliver gifts or materials to guest rooms.",
  staff_parking: "The event team will load in materials before the program.",
};

/** Plans per user and candidate per hour. Best effort: the limiter is per instance. */
export const NEGOTIATION_PLAN_RATE_LIMIT = 10;

/** Window for {@link NEGOTIATION_PLAN_RATE_LIMIT}. */
export const NEGOTIATION_PLAN_RATE_WINDOW_MS = 60 * 60_000;

/** Longest quote group id the plan route accepts. */
export const NEGOTIATION_PLAN_QUOTE_GROUP_MAX_CHARS = 200;

/** Where the modal streams its plan from. */
export const NEGOTIATION_PLAN_ENDPOINT = "/api/negotiation/plan";

/** Least time a status step stays on screen before the next event, so fast steps don't flash. */
export const PLAN_EVENT_MIN_DWELL_MS = 350;

/** Least time between streamed asks on screen, so a burst still lands one row at a time. */
export const PLAN_ASK_DWELL_MS = 120;

/**
 * Mail domains a whole chain shares across its hotels. A sender on one is this hotel only by exact
 * address, since a sister property answering the same RFP writes from the same domain.
 */
export const CHAIN_MAIL_DOMAINS: readonly string[] = [
  "accor.com",
  "bestwestern.com",
  "choicehotels.com",
  "fourseasons.com",
  "hilton.com",
  "hyatt.com",
  "ihg.com",
  "jdvhotels.com",
  "marriott.com",
  "omnihotels.com",
  "radissonhotels.com",
  "wyndham.com",
];

/** An offer whose validity or hold ends within this many days is treated as expiring. */
export const OFFER_EXPIRY_WARNING_DAYS = 3;

/** Why the modal shows no plan, per stage that holds one. `{date}` is a short date. */
export const STAGE_HELD_REASONS = {
  contract_signed: "The contract is signed, so there's nothing to negotiate here.",
  terms_agreed: "Terms are agreed and the contract is underway, so there's nothing to negotiate here.",
  terms_agreed_on: "Terms are agreed and the contract was requested on {date}, so there's nothing to negotiate here.",
  client_direct: "The client is working with this hotel directly, so there's nothing to negotiate here.",
  awaiting_hotel_reply: "A request went to this hotel on {date} and it hasn't replied yet. Wait for its answer.",
  past_event: "This event has already happened, so there's nothing to negotiate here.",
} as const;

/** The code ask that leads a plan on an expired or expiring offer. */
export const RECONFIRM_OFFER_ASK = {
  termLabel: "Offer validity",
  target: "Reconfirm the quoted rate and dates, and extend the offer",
  hotelReason: "The group is still deciding, so a current offer on these dates keeps this program moving.",
  whyExpired: "The offer ran out on {date}, so reconfirm the rate and dates before anything else.",
  whyExpiring: "The offer runs out on {date}, so reconfirm the rate and dates before anything else.",
} as const;

/** A summary that already points the client at the hotel's question. */
export const OPEN_QUESTION_SUMMARY_PATTERN = /\b(question|answer|clarif\w*)\b/i;

/** `oee_quotes.source` of a quote the hotel submitted itself; extracted quotes come from emails the thread already shows. */
export const HOTEL_PORTAL_QUOTE_SOURCE = "venue_quote_calculator";

/** A percent in a sentence about commission ("commissionable at 10%", "7% commission"). */
export const COMMISSION_PERCENT_PATTERN = /(\d{1,2}(?:\.\d+)?)\s*%/g;

/** Text about commission: a sentence in a quote, or a custom ask's label. */
export const COMMISSION_MENTION_PATTERN = /\bcommission/i;

/**
 * Talk of commission (misspelt too), an agency fee, or our IATA number, which an exempt org's hotels
 * never hear. "Rebate" stays out: the revenue rebate to the master account is a playbook ask.
 */
export const COMMISSION_TOPIC_PATTERN = /\bcomm?iss?ion|\bagency[\s-]*fees?\b|\bIATA\b/i;

/**
 * Talk of the service charge, by any of its names (service fee, banquet charge or fee), or of the
 * gratuity, or of what counts toward the F&B minimum: Nowadays never negotiates them, so no ask or
 * prose raises them with a hotel. An admin or resort fee stays askable: its waiver is a playbook ask.
 */
export const SERVICE_CHARGE_TOPIC_PATTERN =
  /\bservice[\s-]*(?:charges?|fees?)\b|\bbanquet[\s-]*(?:charges?|fees?)\b|\bgratuit(?:y|ies)\b|\bcount(?:s|ed|ing)?\b[^.!?]{0,40}\btowards?\b[^.!?]{0,30}\bminimum\b/i;

/**
 * A request, in any free text (the client's note, the model's custom ask, a sent custom ask), on a
 * term never on the ask list: the service charge topics, or commission, which the email asks for on
 * its own, hidden from the list.
 */
export const FORBIDDEN_ASK_PATTERN = new RegExp(
  `${SERVICE_CHARGE_TOPIC_PATTERN.source}|${COMMISSION_TOPIC_PATTERN.source}`,
  "i"
);

/** Each word about commission ("commission", "commissionable"), not "non-commissionable". */
export const COMMISSION_WORD_PATTERN = /(?<!non-?)\bcommission\w*/gi;

/** Rates the hotel says pay no commission at all. */
export const NON_COMMISSIONABLE_PATTERN = /\bnon-?commissionable\b/i;

/** Most characters between a percent and a commission word for the percent to be the commission's. */
export const COMMISSION_ADJACENT_CHARS = 20;

/** Words near a commission percent that mean the hotel turned it down. */
export const COMMISSION_DECLINE_PATTERN =
  /\b(?:declin\w*|unable|cannot|can't|not able|won't|don't|do not|refus\w*|not commissionable|no commission)\b/i;

/**
 * Text with curly apostrophes straightened, so a hotel's "can’t" reads as "can't" to every
 * commission pattern.
 *
 * @param text - A hotel's own words.
 * @returns The text with ASCII apostrophes.
 */
export function straightApostrophes(text: string): string {
  return text.replace(/[’‘]/g, "'");
}

/** Characters around a commission percent where a decline word marks it declined. */
export const COMMISSION_DECLINE_WINDOW_CHARS = 30;

/** Contract terms that are never a must at quote stage unless an org clause requires them. */
export const QUOTE_STAGE_NICE_IDS: readonly string[] = ["attrition_slippage", "comp_rooms"];

/** The asks that move money, which take a must attrition gives up. */
export const MONEY_LEVER_IDS: readonly string[] = [
  "room_rate",
  "fb_minimum_reduction",
  "resort_fee_waiver",
  "meeting_room_rental_waiver",
  "event_tech_fee_waiver",
];

/** Hotel-facing text that talks about the client's budget, which the hotel hears of only when the client shares it. */
export const BUDGET_MENTION_PATTERN = /\bbudget/i;

/** A claim that this offer's rate is the highest of the client's quotes. */
export const HIGHEST_CLAIM_PATTERN =
  /\b(?:highest|most expensive|priciest|ranks? (?:first|1st|top)|above (?:the |all )?(?:others|other (?:quotes|proposals|offers)))\b/i;

/** A meeting-room fee line that is the room rental itself ("Rental", "Room rental", "Meeting space fee"). */
export const RENTAL_FEE_NAME_PATTERN =
  /\brental\b|\broom rent\b|\b(?:meeting|function|event)\s+(?:room|space)\s+(?:fee|charge)s?\b/i;

/** A price beside a listed perk ("VIP amenities at USD 45 each"), which means the hotel sells it rather than gives it. */
const PRICED_PERK = {
  only: /(?:\$|\b(?:USD|MXN|EUR|GBP|CAD)\b)\s*\d|\bat an? (?:additional |extra )?(?:cost|charge|fee)\b|\bfor a fee\b/i,
  unless: /\b(?:complimentary|free|included|at no (?:additional |extra )?(?:charge|cost))\b/i,
};

/**
 * Where a grant covers that makes it no grant of the concession: wifi only in the lobby or common
 * areas leaves guest rooms and meeting rooms to ask for.
 */
export const GRANT_SCOPE_EXCLUSIONS: Partial<Record<NegotiationConcessionId, { only: RegExp; unless: RegExp }>> = {
  complimentary_internet: {
    only: /\b(?:lobby|common areas?|public (?:areas?|spaces?))\b/i,
    unless: /\b(?:guest\s*rooms?|sleeping rooms?|meeting|in[- ]room)\b/i,
  },
  welcome_drinks: PRICED_PERK,
  welcome_amenities: PRICED_PERK,
  vip_amenities: PRICED_PERK,
};

/** Characters around a grant, within its clause, that {@link GRANT_SCOPE_EXCLUSIONS} reads. */
export const GRANT_SCOPE_WINDOW_CHARS = 60;

/** The hotel's AV comes from an outside company it names, so an in-house AV discount doesn't apply. */
export const THIRD_PARTY_AV_PATTERN =
  /\b(?:AV|A\/V|audio[- ]?visual)\b(?:\s+services?)?\s+(?:is|are)\s+(?:provided|handled|managed)\s+by\b|\bthird[- ]party\s+(?:AV|A\/V|audio)/i;

/** A sentence naming the hotel's own AV, which keeps the in-house discount in play. */
export const IN_HOUSE_AV_PATTERN = /\bin[- ]house\b/i;

/** "15 VIP suite upgrades", "One (1) suite upgrade", "Two Havana suite upgrades": captures the count. */
export const OFFERED_SUITES_PATTERN =
  /\b(\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten)\s*(?:\(\d{1,3}\)\s*)?(?:[A-Za-z]+\s+)?suite\s+upgrades?\b/i;

/** "10% off published AV", "(20%) discount for the in-house audio visual": captures the percent. */
export const OFFERED_AV_DISCOUNT_PATTERN =
  /\(?(\d{1,2})\s*%\)?\s*(?:off|discount)\b[^.\n%]{0,40}?\b(?:AV|A\/V|audio[- ]?visual)\b/i;

/** "20% discount on SPA treatments", "15% off spa services": captures the percent. */
export const OFFERED_SPA_DISCOUNT_PATTERN = /\(?(\d{1,2})\s*%\)?\s*(?:off|discount)\b[^.;,\n%]{0,40}?\bspa\b/i;

/** Number words a hotel writes counts in. */
export const NUMBER_WORDS: readonly string[] = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
];

/**
 * The client-facing why when the model's can't be shown, in place of the playbook's landing notes.
 * Money levers get their own sentence; the rest read by readiness.
 */
export const FALLBACK_WHY_BY_CONCESSION: Partial<Record<NegotiationConcessionId, string>> = {
  room_rate: "The rate is the biggest line in this quote, and hotels usually come down a few percent when asked.",
  fb_minimum_reduction: "Hotels often lower the F&B minimum by about a fifth when asked.",
  meeting_room_rental_waiver: "Hotels usually waive room rental when the group meets the F&B minimum.",
  attrition_slippage: "Hotels often ease attrition toward 80% cumulative when asked.",
  comp_rooms: "Hotels often give comp rooms on a block this size.",
};

/** {@link FALLBACK_WHY_BY_CONCESSION}'s sentence for every other concession, by readiness. */
export const FALLBACK_WHY_BY_READINESS = {
  high: "Hotels usually grant this when asked.",
  medium: "Hotels sometimes grant this when asked.",
  low: "Hotels rarely grant this, so treat it as a long shot.",
} as const;

/** The thread-state model: cheap and fast, since it only reads and classifies the thread. */
export const THREAD_STATE_MODEL = "claude-haiku-5-5";

/** Effort for the thread-state model. */
export const THREAD_STATE_EFFORT = "low";

/** Output cap for one thread-state answer. */
export const THREAD_STATE_MAX_TOKENS = 3000;

/** How long the plan waits for the thread state before it goes on with code facts only. */
export const THREAD_STATE_TIMEOUT_MS = 10_000;

const ASK_LEVER = String.raw`(?:waive|waiving|reduce|reduction|lower|discount|come down|bring (?:it|that|this|them|the \w+(?: \w+)?) down|decrease|flexibility on|flexible on|improve|sharpen|match)`;
const ASK_TERM = String.raw`(?:rates?|resort fee|fees?|minimum|f&b|food and beverage|rental|room fee|price|pricing|attrition|deposit|cancellation|concessions?|parking|wifi|internet|service charge)`;
/** A Nowadays message asking the hotel to move a term. */
export const NEGOTIATION_ASK_PATTERN = new RegExp(
  String.raw`\b${ASK_LEVER}\b[^.?!\n]{0,60}\b${ASK_TERM}\b|\b${ASK_TERM}\b[^.?!\n]{0,40}\b(?:waived|reduced|lowered|discounted)\b|\bconcession (?:request|asks?)\b|\bconcessions we (?:asked|requested)\b`,
  "i"
);

/** Bumped whenever the prompt or the schema changes, so cached readings from an older one never stand. */
export const THREAD_STATE_EXTRACTOR_VERSION = "3";

/** Thread readings kept in process; the oldest goes first. */
export const THREAD_STATE_CACHE_MAX_ENTRIES = 200;

/** Thread text the model reads, newest first, about 20k tokens. */
export const THREAD_STATE_INPUT_MAX_CHARS = 80_000;

/** One message's text as the thread-state model reads it. */
export const THREAD_STATE_MESSAGE_MAX_CHARS = 4000;

/** A paraphrase the model writes (a question's topic, a constraint's note); client-facing only. */
export const THREAD_STATE_NOTE_MAX_CHARS = 80;

/** A hotel message this long after the quote's last write means the quote no longer reads the thread. */
export const QUOTE_STALE_AFTER_MS = 86_400_000;

/** How many asks a plan carries when a deadline is days away. */
export const URGENT_PLAN_MAX_ASKS = 5;

/** Asks that make no sense when the hotel has no meeting space for the group. */
const NO_MEETING_SPACE_ASK_IDS: readonly string[] = [
  "meeting_room_rental_waiver",
  "av_discount",
  "setup_fees_waiver",
  "space_reassignment",
  "space_hold_24h",
  "registration_space",
  "outside_av",
  "event_tech_fee_waiver",
  "dedicated_bandwidth",
];

/** Asks a hotel constraint rules out: its AV or catering is not the hotel's to discount or open. */
export const CONSTRAINT_CONFLICTS: Readonly<Record<string, readonly string[]>> = {
  exclusive_av: ["outside_av"],
  third_party_av: ["av_discount", "event_tech_fee_waiver"],
  no_meeting_space: NO_MEETING_SPACE_ASK_IDS,
};

/** Why the modal shows no plan when the thread itself shows the deal is done. `{date}` is a short date. */
export const THREAD_HELD_REASONS = {
  signed: "The thread shows the contract is signed ({date}), so there's nothing to negotiate here.",
  contracting:
    "The thread shows terms agreed and the contract underway ({date}), so there's nothing to negotiate here.",
  lost: "The thread shows the group went elsewhere ({date}), so there's nothing to negotiate here.",
  lost_db: "The client booked another venue for this event, so there's nothing to negotiate here.",
  released: "The thread shows the space was released ({date}), so there's nothing to negotiate here.",
  client_direct:
    "The thread shows the client working with this hotel directly ({date}), so there's nothing to negotiate here.",
  not_a_hotel: "This thread is with a vendor, not a hotel, so there's no hotel offer to negotiate.",
} as const;

/** The summary's lead when the hotel asked something Nowadays hasn't answered. `{topic}` is the question's topic. */
export const OPEN_QUESTION_TOPIC_LEAD = "Answer the hotel's question on {topic} first.";

/** The quote's own terms as the thread-state model reads them. */
export const THREAD_STATE_QUOTE_MAX_CHARS = 3000;

/** One org clause's title as the thread-state model reads it. */
export const THREAD_STATE_CLAUSE_TITLE_MAX_CHARS = 120;

/** The shortest quote from the thread that can settle an org clause; anything shorter matches by chance. */
export const THREAD_STATE_SNIPPET_MIN_CHARS = 8;

/** `event_venue_candidate.customer_decision_status` when the client turned the hotel down. */
export const CLIENT_DECLINED_STATUS = "declined";

/** The thread state block in the plan prompt. */
export const THREAD_STATE_BLOCK_MAX_CHARS = 6000;

/** Two quotes' room nights within this share of each other compare like for like on all-in cost. */
export const ALL_IN_ROOM_NIGHT_TOLERANCE = 0.2;

/** How the client reads each all-in component in the lead ask's why. */
export const ALL_IN_COMPONENT_LABELS = {
  rooms: "the room rate",
  room_fees: "the room fees",
  meeting: "meeting room rental",
  fb: "the F&B minimum",
  misc: "other fees",
} as const;

/** The why of the lead ask code opens on the all-in gap. `{amount}` and `{component}` fill in. */
export const ALL_IN_LEAD_WHY =
  "All-in, this offer sits about {amount} above the middle of your comparable quotes on {component}, so lead with it.";

/** How a competitor figure opens when the hotel reads it: never the other hotel's name, never a count of quotes. */
export const COMPETITOR_FIGURE_LEAD = "Another property we're considering";

/** A hotel-facing count of the client's quotes or the hotels in play ("3 other properties"), which the hotel never reads. */
export const QUOTE_COUNT_PATTERN =
  /\b(?:\d+|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|both|several|many|multiple|a few|a couple|a number)\s+(?:of\s+(?:the\s+|our\s+|your\s+)?)?(?:other\s+|competing\s+|similar\s+)?(?:propert(?:y|ies)|hotels?|quotes?|proposals?|offers?|venues?|bids?)\b/i;

/** A shared all-in budget from this amount up rounds to the thousand. */
export const BUDGET_ROUND_THOUSANDS_FROM = 10_000;

/** A shared all-in budget from this amount up rounds to the hundred; below, to ten. */
export const BUDGET_ROUND_HUNDREDS_FROM = 1_000;

/** A venue budget line under this many of the quote's nightly rates was typed per night, not as a total. */
export const VENUE_BUDGET_PER_NIGHT_BELOW = 3;

/** A target under this share of the quote's all-in is broken data, so the email never shares it. */
export const TARGET_PRICE_FLOOR_OF_ALL_IN = 0.5;

/** Most asks a follow-up round carries: a few focused asks on the hotel's reply. */
export const FOLLOW_UP_MAX_ASKS = 3;

/** A refused resort fee waiver comes back as a cut to this share of the fee. */
export const FOLLOW_UP_RESORT_FEE_SHARE = 0.5;

/** A refused attrition ask comes back at this cumulative pickup. */
export const FOLLOW_UP_ATTRITION_PICKUP = 85;

/** What a follow-up asks for in place of each refused term, and the reasons the hotel and the client read. */
export const FOLLOW_UP_SUBSTITUTES: Partial<
  Record<ThreadTermId, { concessionId: NegotiationConcessionId; hotelReason: string; why: string }>
> = {
  resort_fee_waiver: {
    concessionId: "resort_fee_waiver",
    hotelReason: "If a full waiver isn't possible, halving the fee would close most of the gap.",
    why: "The hotel turned down the waiver, so ask for half the fee off instead.",
  },
  meeting_room_rental_waiver: {
    concessionId: "meeting_room_rental_waiver",
    hotelReason: "Tying the rental to the F&B minimum keeps the hotel's revenue whole.",
    why: "The hotel turned down the waiver, so tie it to the F&B minimum instead.",
  },
  attrition_slippage: {
    concessionId: "attrition_slippage",
    hotelReason: "A little more room on attrition protects both sides if registrations run short.",
    why: "The hotel turned down the attrition ask, so meet it partway at 85% cumulative.",
  },
  room_rate: {
    concessionId: "av_discount",
    hotelReason: "If the rate is set, a break on AV would help close the gap.",
    why: "The hotel held the rate, so move the ask to AV, which costs it less.",
  },
  fb_minimum_reduction: {
    concessionId: "meeting_room_rental_waiver",
    hotelReason: "If the minimum is set, waiving the rental against it would help close the gap.",
    why: "The hotel held the F&B minimum, so ask for the rental against it instead.",
  },
};

/** The why of the one ask a follow-up keeps when the hotel called its offer its best. */
export const FOLLOW_UP_PUSH_WHY = "The hotel called this its best offer, so push once more on a different term.";

/** An F&B fee line that is the service charge or gratuity. */
export const SERVICE_CHARGE_NAME_PATTERN = /\bservice\b|\bgratuit|\bbanquet charge\b/i;

/** An offer whose all-in cost ranks this low or better among comparable quotes may hear it is on the shortlist. */
export const SHORTLIST_TOP_ALL_IN = 3;

/** Hotel-facing text that says the hotel is on the client's shortlist. */
export const SHORTLIST_CLAIM_PATTERN = /\bshort-?list/i;

/** Longest "anything specific?" note the client may write. */
export const PRIORITY_TEXT_MAX_CHARS = 500;

/** Output cap for reading the client's note: thinking plus every id and short label a full note names. */
export const PRIORITY_MAX_TOKENS = 4_000;

/** Reasoning effort for reading the client's note: matching, not planning. */
export const PRIORITY_EFFORT = "low";

/** The note reading's timeout; the modal stops waiting for it well before this. */
export const PRIORITY_TIMEOUT_MS = 20_000;

/**
 * How long the modal waits for the note's reading once the plan is ready, before it shows the plan
 * with the chips alone.
 */
export const PRIORITY_GRACE_MS = 2_500;

/**
 * The wording of a custom request read from the client's note. The label is its own target, so the
 * hotel email reads it as one line; the reason names no one and states no figure.
 */
export const PRIORITY_CUSTOM_ASK_WORDING = {
  why: "You said this matters for this event.",
  hotelReason: "This is a priority for our client.",
} as const;
